// src/pages/campaigns/forms/CampaignKPIsForm.jsx
import React, { useState } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Divider,
  Box,
  InputAdornment,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import {
  BarChart as KpiIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon,
  Lightbulb as TipsIcon,
  ListAlt as KpiListIcon,
  AutoGraph as MetricsIcon,
  Category as CategoryIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

// Import FormSection component
import { FormSection } from '../../../design-system/components/forms';

// KPI categories
const KPI_CATEGORIES = [
  { value: 'awareness', label: 'Awareness' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'conversion', label: 'Conversion' },
  { value: 'retention', label: 'Retention' },
  { value: 'other', label: 'Other' }
];

// Common KPI suggestions by category
const KPI_SUGGESTIONS = {
  awareness: [
    { name: 'Reach', unit: '#', description: 'Total number of unique users who saw content' },
    { name: 'Impressions', unit: '#', description: 'Total number of times content was displayed' },
    { name: 'Brand Awareness Lift', unit: '%', description: 'Increase in brand awareness metrics' },
    { name: 'Share of Voice', unit: '%', description: 'Brand mentions compared to competitors' }
  ],
  engagement: [
    { name: 'Engagement Rate', unit: '%', description: 'Percentage of audience that engaged with content' },
    { name: 'Likes', unit: '#', description: 'Total number of likes across all content' },
    { name: 'Comments', unit: '#', description: 'Total number of comments across all content' },
    { name: 'Shares', unit: '#', description: 'Total number of shares across all content' }
  ],
  conversion: [
    { name: 'Click-through Rate', unit: '%', description: 'Percentage of people who clicked on content' },
    { name: 'Conversion Rate', unit: '%', description: 'Percentage of people who completed desired action' },
    { name: 'Cost per Conversion', unit: '$', description: 'Average cost to acquire one conversion' },
    { name: 'Sales', unit: '#', description: 'Number of sales attributed to campaign' }
  ],
  retention: [
    { name: 'Repeat Purchase Rate', unit: '%', description: 'Percentage of customers who make additional purchases' },
    { name: 'Customer Lifetime Value', unit: '$', description: 'Projected revenue from a customer relationship' },
    { name: 'Churn Rate', unit: '%', description: 'Percentage of customers who stop using products/services' }
  ],
  other: [
    { name: 'Return on Ad Spend', unit: '%', description: 'Revenue generated per dollar spent on advertising' },
    { name: 'Net Promoter Score', unit: '#', description: 'Measure of customer loyalty and satisfaction' }
  ]
};

// Initial KPI form
const initialKpiForm = {
  name: '',
  description: '',
  target: '',
  unit: '#',
  category: 'awareness'
};

// Campaign KPIs form component
const CampaignKPIsForm = ({
  campaignData,
  onChange,
  errors = {},
  touched = {}
}) => {
  const theme = useTheme();
  const [kpiForm, setKpiForm] = useState({...initialKpiForm});
  const [editingIndex, setEditingIndex] = useState(null);

  // Get KPIs from campaign data
  const kpis = campaignData.kpis || [];

  // Handle KPI form change
  const handleKpiFormChange = (e) => {
    const { name, value } = e.target;
    setKpiForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle category change - update suggestions
  const handleCategoryChange = (e) => {
    const category = e.target.value;
    setKpiForm(prev => ({
      ...prev,
      category
    }));
  };
  
  // Handle suggestion select
  const handleSuggestionSelect = (suggestion) => {
    setKpiForm({
      ...kpiForm,
      name: suggestion.name,
      description: suggestion.description,
      unit: suggestion.unit
    });
  };

  // Handle add KPI
  const handleAddKpi = () => {
    // Validate form
    if (!kpiForm.name || !kpiForm.target) {
      return;
    }

    const newKpi = {
      ...kpiForm,
      target: parseFloat(kpiForm.target) || 0,
      current: 0, // Initialize current value to 0
      tracking: {
        lastUpdated: new Date().toISOString()
      }
    };

    // If editing, update existing KPI
    if (editingIndex !== null) {
      const updatedKpis = [...kpis];
      // Keep _id if present
      if (updatedKpis[editingIndex]._id) {
        newKpi._id = updatedKpis[editingIndex]._id;
      }
      updatedKpis[editingIndex] = newKpi;
      onChange('kpis', updatedKpis);
      setEditingIndex(null);
    } else {
      // Add new KPI
      onChange('kpis', [...kpis, newKpi]);
    }

    // Reset form
    setKpiForm({...initialKpiForm});
  };

  // Handle edit KPI
  const handleEditKpi = (index) => {
    const kpi = kpis[index];
    setKpiForm({
      name: kpi.name,
      description: kpi.description || '',
      target: kpi.target.toString(),
      unit: kpi.unit || '#',
      category: kpi.category || 'awareness'
    });
    setEditingIndex(index);
  };

  // Handle delete KPI
  const handleDeleteKpi = (index) => {
    const updatedKpis = [...kpis];
    updatedKpis.splice(index, 1);
    onChange('kpis', updatedKpis);

    // If we're editing this KPI, reset form
    if (editingIndex === index) {
      setKpiForm({...initialKpiForm});
      setEditingIndex(null);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setKpiForm({...initialKpiForm});
    setEditingIndex(null);
  };

  // Function to render the KPI form
  const renderKpiForm = () => (
    <Card variant="outlined">
      <CardHeader 
        title={editingIndex !== null ? "Edit KPI" : "Add New KPI"} 
        avatar={<KpiIcon />}
      />
      <CardContent>
        <Grid container spacing={2}>
          {/* KPI Category */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel 
                id="category-label"
                error={Boolean(errors?.['kpis.category'])}
              >
                KPI Category
              </InputLabel>
              <Select
                labelId="category-label"
                id="category"
                name="category"
                value={kpiForm.category}
                onChange={handleCategoryChange}
                label="KPI Category"
                error={Boolean(errors?.['kpis.category'])}
              >
                {KPI_CATEGORIES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* KPI Name */}
          <Grid item xs={12} md={6}>
            <TextField
              label="KPI Name"
              name="name"
              value={kpiForm.name}
              onChange={handleKpiFormChange}
              fullWidth
              required
              error={Boolean(errors?.['kpis.name'])}
              helperText={errors?.['kpis.name'] || ''}
              InputLabelProps={{
                error: Boolean(errors?.['kpis.name']),
                required: true,
              }}
            />
          </Grid>

          {/* KPI Target Value */}
          <Grid item xs={12} md={6}>
            <TextField
              label="Target Value"
              name="target"
              type="number"
              value={kpiForm.target}
              onChange={handleKpiFormChange}
              fullWidth
              required
              error={Boolean(errors?.['kpis.target'])}
              helperText={errors?.['kpis.target'] || ''}
              InputLabelProps={{
                error: Boolean(errors?.['kpis.target']),
                required: true,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {kpiForm.unit}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* KPI Unit */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel 
                id="unit-label"
                error={Boolean(errors?.['kpis.unit'])}
              >
                Unit
              </InputLabel>
              <Select
                labelId="unit-label"
                id="unit"
                name="unit"
                value={kpiForm.unit}
                onChange={handleKpiFormChange}
                label="Unit"
                error={Boolean(errors?.['kpis.unit'])}
              >
                <MenuItem value="#">Number (#)</MenuItem>
                <MenuItem value="%">Percentage (%)</MenuItem>
                <MenuItem value="$">Currency ($)</MenuItem>
                <MenuItem value="hrs">Hours</MenuItem>
                <MenuItem value="pts">Points</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* KPI Description */}
          <Grid item xs={12}>
            <TextField
              label="Description"
              name="description"
              value={kpiForm.description}
              onChange={handleKpiFormChange}
              fullWidth
              multiline
              rows={2}
              error={Boolean(errors?.['kpis.description'])}
              helperText={errors?.['kpis.description'] || ''}
              InputLabelProps={{
                error: Boolean(errors?.['kpis.description']),
              }}
            />
          </Grid>
        </Grid>
      </CardContent>
      <CardActions>
        <Button
          variant="contained"
          startIcon={editingIndex !== null ? <EditIcon /> : <AddIcon />}
          onClick={handleAddKpi}
          disabled={!kpiForm.name || !kpiForm.target}
        >
          {editingIndex !== null ? "Update KPI" : "Add KPI"}
        </Button>
        {editingIndex !== null && (
          <Button
            variant="outlined"
            onClick={handleCancelEdit}
          >
            Cancel
          </Button>
        )}
      </CardActions>
    </Card>
  );

  // Function to render KPI suggestions
  const renderKpiSuggestions = () => (
    <>
      <Typography variant="subtitle2" gutterBottom>
        Suggested KPIs for {KPI_CATEGORIES.find(c => c.value === kpiForm.category)?.label}:
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        {KPI_SUGGESTIONS[kpiForm.category]?.map((suggestion, index) => (
          <Chip
            key={index}
            label={suggestion.name}
            onClick={() => handleSuggestionSelect(suggestion)}
            clickable
            variant="outlined"
          />
        ))}
      </Box>
    </>
  );

  // Function to render the KPI list
  const renderKpiList = () => (
    <>
      <Typography variant="subtitle1" gutterBottom>
        Campaign KPIs ({kpis.length})
      </Typography>
      
      {kpis.length === 0 ? (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          bgcolor: alpha(theme.palette.background.default, 0.5),
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`
        }}>
          <Typography color="textSecondary" gutterBottom>
            No KPIs defined yet
          </Typography>
          <Typography variant="body2" color={errors?.kpis ? "error" : "textSecondary"} sx={{ fontWeight: errors?.kpis ? 'bold' : 'normal' }}>
            {errors?.kpis || "Add key performance indicators to track campaign success"}
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {kpis.map((kpi, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card 
                variant="outlined"
                sx={{
                  height: '100%',
                  bgcolor: editingIndex === index ? alpha(theme.palette.primary.main, 0.05) : undefined,
                  borderColor: editingIndex === index ? theme.palette.primary.main : undefined
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1">
                      {kpi.name}
                    </Typography>
                    <Chip 
                      label={kpi.category} 
                      size="small" 
                      color={
                        kpi.category === 'awareness' ? 'primary' :
                        kpi.category === 'engagement' ? 'secondary' :
                        kpi.category === 'conversion' ? 'success' :
                        kpi.category === 'retention' ? 'info' : 'default'
                      }
                    />
                  </Box>
                  
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {kpi.description || 'No description provided'}
                  </Typography>
                  
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
                    <Typography>
                      Target: <strong>{kpi.target} {kpi.unit}</strong>
                    </Typography>
                    <Typography>
                      Current: <strong>{kpi.current || 0} {kpi.unit}</strong>
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditKpi(index)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteKpi(index)}
                  >
                    Delete
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </>
  );

  // Function to render KPI tips
  const renderKpiTips = () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: alpha(theme.palette.info.main, 0.1), 
      borderRadius: 1
    }}>
      <Typography variant="subtitle2" gutterBottom>
        Tips for Effective KPIs:
      </Typography>
      <Typography variant="body2">
        • Set SMART goals: Specific, Measurable, Achievable, Relevant, Time-bound
      </Typography>
      <Typography variant="body2">
        • Align KPIs with your overall campaign objectives
      </Typography>
      <Typography variant="body2">
        • Focus on quality over quantity - choose a few meaningful KPIs
      </Typography>
      <Typography variant="body2">
        • Include a mix of KPIs across different categories
      </Typography>
      <Typography variant="body2">
        • Regularly monitor and update your KPIs throughout the campaign
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Section 1: Add/Edit KPI */}
      <FormSection
        title="Define Campaign KPIs"
        description="Create key performance indicators to measure your campaign's success."
        icon={<MetricsIcon />}
        tooltip="KPIs help you track and measure your campaign performance against specific goals."
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {renderKpiForm()}
          </Grid>
          <Grid item xs={12}>
            {renderKpiSuggestions()}
          </Grid>
        </Grid>
      </FormSection>

      {/* Section 2: KPI List */}
      <FormSection
        title="Campaign KPI Overview"
        description="View and manage all KPIs defined for this campaign."
        icon={<KpiListIcon />}
        tooltip="Review all defined KPIs and track progress toward campaign goals."
      >
        {renderKpiList()}
      </FormSection>

      {/* Section 3: KPI Best Practices */}
      <FormSection
        title="KPI Best Practices"
        description="Tips for creating effective, measurable KPIs."
        icon={<TipsIcon />}
        tooltip="Follow these guidelines to create meaningful KPIs that align with your campaign objectives."
      >
        {renderKpiTips()}
      </FormSection>
    </>
  );
};

export default CampaignKPIsForm;